import {
  createContext,
  forwardRef,
  useContext,
  useMemo,
  type CSSProperties,
  type HTMLAttributes,
  type ImgHTMLAttributes,
  type ReactNode,
} from 'react'

/* A small Remotion-compatible surface for the tutorial films, so compositions
   ported from the landing site (tenfour-site-design-system/src/remotion) need
   only their import lines swapped. There is no Remotion runtime here: the
   TutorialPlayer owns the clock and publishes the current frame through
   FrameProvider; everything below is a pure function of that frame.

   The math follows remotion 4.0.500 (interpolate's segment lookup,
   per-segment easing and extrapolation; interpolateColors' clamped, rounded
   rgba output; spring's stepped analytic oscillator, which collapses to the
   closed form used here). Nothing checks the two against each other: a film
   ported from the landing is checked by eye against its source. */

export interface VideoConfig {
  fps: number
  width: number
  height: number
  durationInFrames: number
}

interface FrameState {
  frame: number
  config: VideoConfig
}

const DEFAULT_CONFIG: VideoConfig = { fps: 60, width: 1200, height: 740, durationInFrames: 1 }

const FrameContext = createContext<FrameState | null>(null)

/** Publishes the frame a film renders. The player (and TutorialStill) wrap
    every composition in one; nest your own only to render a film elsewhere. */
export function FrameProvider({
  frame,
  fps,
  width,
  height,
  durationInFrames,
  children,
}: VideoConfig & { frame: number; children?: ReactNode }) {
  const config = useMemo(() => ({ fps, width, height, durationInFrames }), [fps, width, height, durationInFrames])
  const value = useMemo(() => ({ frame, config }), [frame, config])
  return <FrameContext.Provider value={value}>{children}</FrameContext.Provider>
}

/** The frame being rendered, relative to the enclosing Sequence. 0 outside a provider. */
export function useCurrentFrame(): number {
  return useContext(FrameContext)?.frame ?? 0
}

/** The composition's size, rate and length (not the enclosing Sequence's). */
export function useVideoConfig(): VideoConfig {
  return useContext(FrameContext)?.config ?? DEFAULT_CONFIG
}

/* --- Elements --------------------------------------------------------------- */

type RemotionImgExtras = {
  maxRetries?: number
  pauseWhenLoading?: boolean
  delayRenderRetries?: number
  delayRenderTimeoutInMilliseconds?: number
  showInTimeline?: boolean
}

/** A plain <img>: never draggable, decoded off the main thread. Remotion-only
    props are accepted and dropped. */
export const Img = forwardRef<HTMLImageElement, ImgHTMLAttributes<HTMLImageElement> & RemotionImgExtras>(function Img(
  {
    alt = '',
    maxRetries: _maxRetries,
    pauseWhenLoading: _pauseWhenLoading,
    delayRenderRetries: _delayRenderRetries,
    delayRenderTimeoutInMilliseconds: _delayRenderTimeout,
    showInTimeline: _showInTimeline,
    ...props
  },
  ref,
) {
  return <img ref={ref} alt={alt} draggable={false} decoding="async" {...props} />
})

const FILL: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}

/** A full-bleed, column-flex layer, as in Remotion. */
export const AbsoluteFill = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(function AbsoluteFill(
  { style, ...props },
  ref,
) {
  return <div ref={ref} style={style ? { ...FILL, ...style } : FILL} {...props} />
})

export interface SequenceProps {
  from?: number
  durationInFrames?: number
  children?: ReactNode
  /** 'absolute-fill' (default) wraps children in an AbsoluteFill; 'none' renders them bare. */
  layout?: 'absolute-fill' | 'none'
  /** Frames before `from` during which children mount hidden, frozen at their frame 0. */
  premountFor?: number
  style?: CSSProperties
  className?: string
  /** Accepted for Remotion compatibility; unused. */
  name?: string
  showInTimeline?: boolean
}

/** Time-shifts its children: inside, useCurrentFrame() counts from `from`.
    Renders nothing outside [from, from + durationInFrames). */
export function Sequence({
  from = 0,
  durationInFrames = Infinity,
  children,
  layout = 'absolute-fill',
  premountFor = 0,
  style,
  className,
}: SequenceProps) {
  const parent = useContext(FrameContext)
  const outer = parent?.frame ?? 0
  const config = parent?.config ?? DEFAULT_CONFIG
  const local = outer - from
  const inside = local >= 0 && local < durationInFrames
  const premounting = !inside && premountFor > 0 && local < 0 && local >= -premountFor
  const value = useMemo(
    () => ({ frame: premounting ? 0 : local, config }),
    [premounting, local, config],
  )
  if (!inside && !premounting) return null
  const hidden: CSSProperties | undefined = premounting ? { display: 'none', pointerEvents: 'none' } : undefined
  const body = <FrameContext.Provider value={value}>{children}</FrameContext.Provider>
  if (layout === 'none') {
    return hidden ? <div style={hidden}>{body}</div> : body
  }
  return (
    <AbsoluteFill className={className} style={hidden ? { ...style, ...hidden } : style}>
      {body}
    </AbsoluteFill>
  )
}

/* --- interpolate ------------------------------------------------------------ */

export type ExtrapolateType = 'clamp' | 'extend' | 'identity' | 'wrap'
export type EasingFunction = (t: number) => number

export interface InterpolateOptions {
  extrapolateLeft?: ExtrapolateType
  extrapolateRight?: ExtrapolateType
  /** One easing for every segment, or one per segment (inputRange.length - 1). */
  easing?: EasingFunction | readonly EasingFunction[]
  /** Snap the input down to a multiple of this step before mapping. */
  posterize?: number
}

const linear: EasingFunction = (t) => t

function checkRange(inputRange: readonly number[], outputRange: readonly unknown[]) {
  if (inputRange.length !== outputRange.length) {
    throw new Error(`inputRange (${inputRange.length}) and outputRange (${outputRange.length}) must have the same length`)
  }
  for (let i = 0; i < inputRange.length; i++) {
    if (!Number.isFinite(inputRange[i])) throw new Error(`inputRange must contain only finite numbers, but got [${inputRange.join(',')}]`)
    if (i > 0 && !(inputRange[i] > inputRange[i - 1])) {
      throw new Error(`inputRange must be strictly monotonically increasing but got [${inputRange.join(',')}]`)
    }
  }
}

function mapSegment(
  input: number,
  [inputMin, inputMax]: readonly [number, number],
  [outputMin, outputMax]: readonly [number, number],
  easing: EasingFunction,
  extrapolateLeft: ExtrapolateType,
  extrapolateRight: ExtrapolateType,
) {
  let result = input
  if (result < inputMin) {
    if (extrapolateLeft === 'identity') return result
    if (extrapolateLeft === 'clamp') result = inputMin
    else if (extrapolateLeft === 'wrap') {
      const range = inputMax - inputMin
      result = ((((result - inputMin) % range) + range) % range) + inputMin
    }
  }
  if (result > inputMax) {
    if (extrapolateRight === 'identity') return result
    if (extrapolateRight === 'clamp') result = inputMax
    else if (extrapolateRight === 'wrap') {
      const range = inputMax - inputMin
      result = ((((result - inputMin) % range) + range) % range) + inputMin
    }
  }
  if (outputMin === outputMax) return outputMin
  result = (result - inputMin) / (inputMax - inputMin)
  result = easing(result)
  return result * (outputMax - outputMin) + outputMin
}

/** Remotion's segment lookup: the first i in 1…n−2 with inputRange[i] ≥ input picks segment i−1. */
function findSegment(input: number, inputRange: readonly number[]) {
  let i = 1
  for (; i < inputRange.length - 1; ++i) {
    if (inputRange[i] >= input) break
  }
  return i - 1
}

/** Maps `input` through piecewise-linear (optionally eased) segments. Defaults
    to 'extend' on both sides, like Remotion; pass `{ extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp' }` to hold the ends. */
export function interpolate(
  input: number,
  inputRange: readonly number[],
  outputRange: readonly number[],
  options?: InterpolateOptions,
): number {
  checkRange(inputRange, outputRange)
  if (inputRange.length === 1) return outputRange[0]
  const value = options?.posterize ? Math.floor(input / options.posterize) * options.posterize : input
  const segment = findSegment(value, inputRange)
  const easingOption = options?.easing
  const easing = easingOption === undefined ? linear : typeof easingOption === 'function' ? easingOption : easingOption[segment] ?? linear
  return mapSegment(
    value,
    [inputRange[segment], inputRange[segment + 1]],
    [outputRange[segment], outputRange[segment + 1]],
    easing,
    options?.extrapolateLeft ?? 'extend',
    options?.extrapolateRight ?? 'extend',
  )
}

/* --- interpolateColors ------------------------------------------------------ */

const NUMBER = '[-+]?\\d*\\.?\\d+'
const RGB = new RegExp(`rgb\\(\\s*(${NUMBER})\\s*,\\s*(${NUMBER})\\s*,\\s*(${NUMBER})\\s*\\)`)
const RGBA = new RegExp(`rgba\\(\\s*(${NUMBER})\\s*,\\s*(${NUMBER})\\s*,\\s*(${NUMBER})\\s*,\\s*(${NUMBER})\\s*\\)`)
const NAMED: Record<string, [number, number, number, number]> = {
  transparent: [0, 0, 0, 0],
  black: [0, 0, 0, 255],
  white: [255, 255, 255, 255],
}

const channel255 = (text: string) => Math.min(255, Math.max(0, Number.parseInt(text, 10)))
const alpha255 = (text: string) => {
  const n = Number.parseFloat(text)
  return n < 0 ? 0 : n > 1 ? 255 : Math.round(n * 255)
}

/** [r, g, b, a] with every channel 0–255, parsed the way Remotion does. */
function parseColor(color: string): [number, number, number, number] {
  const named = NAMED[color]
  if (named) return named
  let m = /^#([0-9a-fA-F]{6})$/.exec(color)
  if (m) {
    const n = Number.parseInt(m[1], 16)
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255, 255]
  }
  m = /^#([0-9a-fA-F]{8})$/.exec(color)
  if (m) {
    const n = Number.parseInt(m[1], 16) >>> 0
    return [(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255]
  }
  m = /^#([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])?$/.exec(color)
  if (m) {
    const d = (h: string) => Number.parseInt(h + h, 16)
    return [d(m[1]), d(m[2]), d(m[3]), m[4] ? d(m[4]) : 255]
  }
  m = RGBA.exec(color)
  if (m) return [channel255(m[1]), channel255(m[2]), channel255(m[3]), alpha255(m[4])]
  m = RGB.exec(color)
  if (m) return [channel255(m[1]), channel255(m[2]), channel255(m[3]), 255]
  throw new Error(`invalid color string ${color} provided`)
}

/** Interpolates each channel separately (always clamped) and returns
    `rgba(r, g, b, a)` with rounded channels and a 3-decimal alpha. */
export function interpolateColors(
  input: number,
  inputRange: readonly number[],
  outputRange: readonly string[],
  options?: Pick<InterpolateOptions, 'easing' | 'posterize'>,
): string {
  checkRange(inputRange, outputRange)
  // Alpha travels as 0–1 (Remotion divides by 255 before interpolating).
  const colors = outputRange.map(parseColor).map(([r, g, b, a]) => [r, g, b, a / 255])
  const at = (index: number) =>
    interpolate(input, inputRange, colors.map((c) => c[index]), {
      easing: options?.easing,
      posterize: options?.posterize,
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    })
  const alpha = Number(at(3).toFixed(3))
  return `rgba(${Math.round(at(0))}, ${Math.round(at(1))}, ${Math.round(at(2))}, ${alpha})`
}

/* --- spring ----------------------------------------------------------------- */

export interface SpringConfig {
  damping: number
  mass: number
  stiffness: number
  overshootClamping: boolean
}

const SPRING_DEFAULTS: SpringConfig = { damping: 10, mass: 1, stiffness: 100, overshootClamping: false }
/** Remotion steps its oscillator once per frame and never by more than 64 ms. */
const MAX_STEP_SECONDS = 0.064

/** Seconds of simulated time Remotion has advanced by `frame`, including its
    per-step cap (which only bites below ~15.6 fps, or ~31 fps on a fractional frame). */
function simulatedSeconds(frame: number, fps: number) {
  const clamped = Math.max(0, frame)
  const whole = Math.floor(clamped)
  const rest = clamped - whole
  if (whole === 0) return Math.min(rest / fps, MAX_STEP_SECONDS)
  return (whole - 1) * Math.min(1 / fps, MAX_STEP_SECONDS) + Math.min((1 + rest) / fps, MAX_STEP_SECONDS)
}

/** Position (0→1) of Remotion's spring at `frame`. Stepping the exact
    oscillator solution frame by frame composes to this closed form; like
    Remotion, any ζ ≥ 1 takes the critically-damped branch. */
function springPosition(frame: number, fps: number, config: SpringConfig) {
  if (config.damping <= 0) throw new Error('Spring damping must be greater than 0')
  const t = simulatedSeconds(frame, fps)
  const { damping: c, mass: m, stiffness: k } = config
  const zeta = c / (2 * Math.sqrt(k * m))
  const omega0 = Math.sqrt(k / m)
  if (zeta < 1) {
    const omega1 = omega0 * Math.sqrt(1 - zeta * zeta)
    const envelope = Math.exp(-zeta * omega0 * t)
    return 1 - envelope * (Math.sin(omega1 * t) * ((zeta * omega0) / omega1) + Math.cos(omega1 * t))
  }
  return 1 - Math.exp(-omega0 * t) * (1 + omega0 * t)
}

const naturalDurations = new Map<string, number>()

/** Remotion's measureSpring: frames until the spring stays within `threshold` of rest for 20 frames. */
export function measureSpring({
  fps,
  config = {},
  threshold = 0.005,
}: {
  fps: number
  config?: Partial<SpringConfig>
  threshold?: number
}): number {
  if (threshold === 0) return Infinity
  if (threshold === 1) return 0
  const merged = { ...SPRING_DEFAULTS, ...config }
  const key = [fps, merged.damping, merged.mass, merged.stiffness, threshold].join('-')
  const cached = naturalDurations.get(key)
  if (cached !== undefined) return cached
  const off = (f: number) => Math.abs(springPosition(f, fps, merged) - 1)
  let frame = 0
  while (off(frame) >= threshold) frame++
  let finished = frame
  for (let i = 0; i < 20; i++) {
    frame++
    if (off(frame) >= threshold) {
      i = 0
      finished = frame + 1
    }
  }
  naturalDurations.set(key, finished)
  return finished
}

export interface SpringOptions {
  frame: number
  fps: number
  config?: Partial<SpringConfig>
  from?: number
  to?: number
  /** Stretch or squash the spring to settle in this many frames. */
  durationInFrames?: number
  durationRestThreshold?: number
  delay?: number
  reverse?: boolean
}

/** Remotion's spring(): a damped oscillator from `from` to `to`, sampled at `frame`. */
export function spring({
  frame,
  fps,
  config = {},
  from = 0,
  to = 1,
  durationInFrames,
  durationRestThreshold,
  delay = 0,
  reverse = false,
}: SpringOptions): number {
  const merged = { ...SPRING_DEFAULTS, ...config }
  const natural =
    reverse || durationInFrames !== undefined
      ? measureSpring({ fps, config: merged, threshold: durationRestThreshold })
      : 0
  const reversed = reverse ? (durationInFrames ?? natural) - frame : frame
  const delayed = reversed + (reverse ? delay : -delay)
  const stretched = durationInFrames === undefined ? delayed : delayed / (durationInFrames / natural)
  if (durationInFrames && delayed > durationInFrames) return to
  const current = springPosition(stretched, fps, merged)
  const inner = merged.overshootClamping ? (to >= from ? Math.min(current, to) : Math.max(current, to)) : current
  return from === 0 && to === 1 ? inner : interpolate(inner, [0, 1], [from, to])
}
